import React, { useEffect, useRef, useState } from "react";
import Lottie from "lottie-react";
import {
  startSession,
  endSession,
  getGameId,
  getMyGameSettings,
} from "../../services/GameService";
import { speak, primeVoices } from "../../utils/speech";
import GameOverScreen from "../GameOverScreen";
import EdgeFlowers from "../EdgeFlowers";
import { usePlayClock } from "../common/usePlayClock";
import fireworksData from "./animations/fireworks.json";
import fireworksSound from "./sounds/fireworks.mp3";
import "./SerialSubtractionForm.css";

const DEFAULT_STEP = 7;

// Clinically-reasonable randomization ranges (#113). With no level config the
// exercise picks a fresh start + step each game so the patient gets new math
// every run instead of memorizing the fixed 100 − 7 chain.
export const START_RANGE = { min: 90, max: 120 };
export const STEP_CHOICES = [3, 4, 6, 7, 8, 9];

// A random integer start within START_RANGE (inclusive). `rng` is injectable so
// tests can seed it deterministically.
export const randomStart = (rng = Math.random) =>
  START_RANGE.min + Math.floor(rng() * (START_RANGE.max - START_RANGE.min + 1));

// A random subtraction step from the small clinically-sensible set.
export const randomStep = (rng = Math.random) =>
  STEP_CHOICES[Math.floor(rng() * STEP_CHOICES.length)];

// A random integer in [min, max] (inclusive). `rng` is injectable for tests.
export const randomInRange = (min, max, rng = Math.random) =>
  min + Math.floor(rng() * (max - min + 1));

// Parse a level's difficulty band from its level_parameters JSON (#118). Lower
// levels start with small numbers and small steps (only a couple of
// subtractions); higher levels start bigger with larger steps, so the chain
// runs much longer. Levels store a start range + allowed steps, e.g.
//   {"startMin": 5, "startMax": 9, "steps": [2, 3]}
// so each game still randomizes within the band. Returns null when the level is
// absent or malformed so the caller keeps the global default behaviour.
export const parseLevelMath = (level) => {
  if (!level || !level.level_parameters) return null;
  try {
    const p = JSON.parse(level.level_parameters);
    const { startMin, startMax } = p;
    const steps = Array.isArray(p.steps)
      ? p.steps.filter((s) => Number.isFinite(s) && s > 0)
      : [];
    if (!Number.isFinite(startMin) || !Number.isFinite(startMax)) return null;
    if (startMin < 1 || startMax < startMin) return null;
    if (!steps.length) return null;
    return { startMin, startMax, steps };
  } catch (e) {
    return null;
  }
};

// Voice-guided serial subtraction. Audio only: a voice reads "<current> minus
// <step>"; the patient enters the answer; the chain continues from the correct
// value until it can no longer subtract. The question is never shown as text.
// Start/step are randomized each game (#113) unless a level config pins them.
const SerialSubtractionForm = ({ gameRoutePath = "serial-subtraction" }) => {
  const [step, setStep] = useState(DEFAULT_STEP);
  const [current, setCurrent] = useState(null);
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);

  const sessionIdRef = useRef(null);
  const totalRef = useRef(0);
  const correctRef = useRef(0);
  const playClock = usePlayClock();
  const fireworksAudioRef = useRef(new Audio(fireworksSound));

  // Load the session + settings on mount and speak the first question right
  // away. The click that navigated here (a game tile) is a user gesture, so the
  // page already has activation and the browser lets this audio play.
  useEffect(() => {
    let active = true;
    primeVoices();
    (async () => {
      // Fresh randomized assignment each game; overridden by level config below.
      let start = randomStart();
      let st = randomStep();
      try {
        const session = await startSession(gameRoutePath);
        if (session && session.sessionId) {
          sessionIdRef.current = session.sessionId;
        }
        // The player's current level (carried on the session) sets the
        // difficulty band — small numbers/steps low down, bigger ones higher
        // up (#118). Randomize within the band so each game differs.
        const band = parseLevelMath(session && session.level);
        if (band) {
          start = randomInRange(band.startMin, band.startMax);
          st = band.steps[Math.floor(Math.random() * band.steps.length)];
        }

        const gameId = await getGameId(gameRoutePath);
        if (gameId) {
          const settings = await getMyGameSettings(gameId);
          const cfg = (settings && settings.settings) || {};
          // A clinician's explicit per-patient config stays authoritative.
          if (Number.isFinite(cfg.start) && cfg.start > 0) start = cfg.start;
          if (Number.isFinite(cfg.step) && cfg.step > 0) st = cfg.step;
        }
      } catch (e) {
        // fall back to randomized values
      }
      if (!active) return;
      setStep(st);
      setCurrent(start);
      // Play starts with the first question.
      playClock.start();
    })();
    return () => {
      active = false;
      // Mirror FindMiddle's audio cleanup so the celebration sound never
      // outlives the component.
      if (fireworksAudioRef.current) {
        fireworksAudioRef.current.pause();
        fireworksAudioRef.current.currentTime = 0;
      }
    };
  }, [gameRoutePath, playClock]);

  const promptText = current != null ? `${current} minus ${step}` : "";

  // Read the current question aloud whenever it changes — on first load and on
  // each new step. Same approach as the voice-image-recognition game: speaking
  // from an effect keyed on the loaded value (after render commits) plays
  // reliably, including the very first prompt.
  useEffect(() => {
    if (current != null) {
      speak(`${current} minus ${step}`);
      // Each new question starts as "Play"; it becomes "Replay" once tapped.
      setHasPlayed(false);
    }
  }, [current, step]);

  // Play the celebration sound while the fireworks show, and stop it on
  // cleanup. Mirrors FindMiddle's fireworks audio handling.
  useEffect(() => {
    const audio = fireworksAudioRef.current;
    if (showFireworks) {
      audio.loop = true;
      audio.play().catch((error) => {
        console.error("Failed to play fireworks sound:", error);
      });
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
    return () => {
      if (showFireworks) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, [showFireworks]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (feedback || current == null) return;
    const expected = current - step;
    const correct = Number(answer) === expected;
    totalRef.current += 1;
    if (correct) correctRef.current += 1;
    setFeedback({ correct, expected });

    // The chain continues from the correct value until it can't subtract again.
    const chainEnds = expected - step < 0 || expected <= 0;
    // Last answer in: play time stops here, not after the closing pause.
    if (chainEnds) playClock.stop();

    setTimeout(() => {
      setAnswer("");
      setFeedback(null);
      // Continue the chain from the correct value to keep the exercise on track.
      if (chainEnds) {
        setGameOver(true);
        setShowFireworks(true);
        const score = totalRef.current
          ? (correctRef.current / totalRef.current) * 100
          : 0;
        endSession(sessionIdRef.current, score, playClock.elapsedMs());
      } else {
        // The effect keyed on `current` speaks the new question.
        setCurrent(expected);
      }
    }, 1200);
  };

  if (gameOver) {
    return (
      <GameOverScreen
        stats={[`You got ${correctRef.current} of ${totalRef.current} correct!`]}
        showFireworks={showFireworks}
        fireworksAnimation={fireworksData}
      />
    );
  }

  return (
    <div className="serial-subtraction">
      <EdgeFlowers />
      <h1 className="ss-title">Mental Math</h1>

      <button
        type="button"
        className="ss-replay"
        onClick={() => {
          speak(promptText);
          setHasPlayed(true);
        }}
        disabled={!promptText}
      >
        🔊 {hasPlayed ? "Replay" : "Play"}
      </button>

      <form className="ss-form" onSubmit={handleSubmit}>
        <input
          type="number"
          className="ss-input"
          aria-label="Your answer"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          disabled={Boolean(feedback)}
          autoFocus
        />
        <button type="submit" className="ss-submit" disabled={Boolean(feedback)}>
          Enter
        </button>
      </form>

      {feedback && (
        <p
          className={feedback.correct ? "ss-correct" : "ss-incorrect"}
          role="status"
        >
          {feedback.correct
            ? "Correct!"
            : `Not quite — the answer was ${feedback.expected}.`}
        </p>
      )}
    </div>
  );
};

export default SerialSubtractionForm;
